'use client';

import { useEffect, useRef, useState } from 'react';
import { GripVertical, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DevToolsThemeSwitch } from './dev-tools-theme-switch';
import {
  DEV_CONSOLE_SIZE,
  DEV_TOOLS_POSITION_KEY,
  applyDevChordKey,
  initialDevToolsPosition,
  clampDevToolsPosition,
  moveDevConsole,
  parseDevToolsPosition,
  shouldIgnoreDevChordTarget,
  type DevToolsPoint,
} from './dev-tools-model';

function readInitialPosition(): DevToolsPoint {
  const viewport = {
    width: window.innerWidth,
    height: window.innerHeight,
  };
  let stored: DevToolsPoint | null = null;
  try {
    stored = parseDevToolsPosition(
      sessionStorage.getItem(DEV_TOOLS_POSITION_KEY)
    );
  } catch {
    stored = null;
  }
  return stored
    ? clampDevToolsPosition(stored, DEV_CONSOLE_SIZE, viewport)
    : initialDevToolsPosition(viewport);
}

function writePosition(point: DevToolsPoint): void {
  try {
    sessionStorage.setItem(DEV_TOOLS_POSITION_KEY, JSON.stringify(point));
  } catch {
    // Position still sticks for this page load.
  }
}

export function DevConsole() {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<DevToolsPoint | null>(null);
  const consoleRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    start: DevToolsPoint;
    pointerStart: DevToolsPoint;
  } | null>(null);
  const heldRef = useRef(new Set<string>());

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target;
      const editable =
        target instanceof HTMLElement
          ? {
              tagName: target.tagName,
              isContentEditable: target.isContentEditable,
            }
          : null;
      if (shouldIgnoreDevChordTarget(editable)) return;

      const result = applyDevChordKey(heldRef.current, {
        type: event.type === 'keyup' ? 'keyup' : 'keydown',
        key: event.key,
        code: event.code,
      });
      heldRef.current = new Set(result.held);
      if (!result.toggled) return;
      event.preventDefault();
      setPosition((current) => current ?? readInitialPosition());
      setOpen((current) => !current);
    };

    const clearHeld = () => {
      heldRef.current = new Set();
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    window.addEventListener('blur', clearHeld);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
      window.removeEventListener('blur', clearHeld);
    };
  }, []);

  if (!open || !position) return null;

  return (
    <div
      ref={consoleRef}
      role="dialog"
      aria-label="Dev Console"
      className="bg-foreground/70 text-background border-border/50 fixed z-99999 w-65 rounded-lg border font-mono shadow-lg backdrop-blur-xl"
      style={{ left: position.x, top: position.y }}
    >
      <div className="border-border/50 flex items-center border-b">
        <div
          aria-label="Drag"
          className="flex min-w-0 flex-1 cursor-grab touch-none items-center gap-2 px-3 py-2 active:cursor-grabbing"
          onPointerDown={(event) => {
            if (event.button !== 0 || !position) return;
            event.currentTarget.setPointerCapture(event.pointerId);
            dragRef.current = {
              pointerId: event.pointerId,
              start: position,
              pointerStart: { x: event.clientX, y: event.clientY },
            };
          }}
          onPointerMove={(event) => {
            const drag = dragRef.current;
            const consoleElement = consoleRef.current;
            if (
              !drag ||
              drag.pointerId !== event.pointerId ||
              !consoleElement
            ) {
              return;
            }
            const rect = consoleElement.getBoundingClientRect();
            setPosition(
              moveDevConsole({
                start: drag.start,
                pointerStart: drag.pointerStart,
                pointer: { x: event.clientX, y: event.clientY },
                consoleSize: { width: rect.width, height: rect.height },
                viewport: {
                  width: window.innerWidth,
                  height: window.innerHeight,
                },
              })
            );
          }}
          onPointerUp={(event) => {
            if (dragRef.current?.pointerId !== event.pointerId) return;
            dragRef.current = null;
            setPosition((current) => {
              if (current) writePosition(current);
              return current;
            });
          }}
        >
          <GripVertical className="size-4" aria-hidden="true" />
          <span className="text-sm font-medium select-none">Dev Console</span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="mr-1"
          aria-label="Close"
          onClick={() => setOpen(false)}
        >
          <X className="size-4" aria-hidden="true" />
        </Button>
      </div>
      <div className="px-3 py-3">
        <DevToolsThemeSwitch />
      </div>
    </div>
  );
}
